import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Stack,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  MenuItem,
  Typography,
  FormControl,
  Select
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import api from '../services/api';
import debounce from 'lodash/debounce';

// Memoized PlayerSearchInput component
const PlayerSearchInput = React.memo(({ onSearch, value, disabled, setActiveInput, teamNumber, isSearching }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        width: '100%',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <SearchIcon sx={{ color: disabled ? 'text.disabled' : 'primary.main' }} />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => {
          e.preventDefault();
          onSearch(e.target.value);
        }}
        onFocus={(e) => {
          e.preventDefault();
          setActiveInput(teamNumber);
        }}
        placeholder={isSearching ? "Buscando..." : "Buscar jugador..."}
        disabled={disabled}
        style={{
          width: '100%',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: disabled ? 'inherit' : 'white',
          fontSize: '16px',
          padding: '4px 0',
          WebkitAppearance: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
    </Box>
  );
});

// DeckSelector component
const DeckSelector = React.memo(({ player, teamNumber, selectedDeck, setTeam1SelectedDecks, setTeam2SelectedDecks, showAnyDeckOption = false }) => {
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const decksLoadedRef = useRef(false);

  const loadDecks = useCallback(async () => {
    if (!player?.id || decksLoadedRef.current) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/decks/user/${player.id}`, {
        withCredentials: true
      });
      
      // Ordenar los mazos para que el mazo por defecto aparezca primero
      const sortedDecks = response.data.sort((a, b) => {
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        return 0;
      });
      
      setDecks(sortedDecks || []);
      decksLoadedRef.current = true;
      
      // Si hay un mazo seleccionado pero no está en los mazos cargados, limpiar la selección
      if (selectedDeck?.deckId && !sortedDecks.some(d => String(d.id) === String(selectedDeck.deckId))) {
        if (teamNumber === 1) {
          setTeam1SelectedDecks(prev => prev.filter(d => d.playerId !== player.id));
        } else {
          setTeam2SelectedDecks(prev => prev.filter(d => d.playerId !== player.id));
        }
      }
    } catch (error) {
      console.warn(`Unable to load decks for user ${player.id}:`, error);
      setError('No se pudieron cargar los mazos');
      setDecks([]);
    }
    setLoading(false);
  }, [player?.id, teamNumber, selectedDeck?.deckId, setTeam1SelectedDecks, setTeam2SelectedDecks]);

  useEffect(() => {
    loadDecks();
  }, [loadDecks]);

  const handleDeckChange = useCallback((event) => {
    const deckId = event.target.value;
    
    if (deckId === 'any') {
      // Si se selecciona "Cualquier Mazo"
      const newDeckData = {
        playerId: player.id,
        deckId: null,
        deckName: "Cualquier Mazo",
        deckRaza: null
      };

      if (teamNumber === 1) {
        setTeam1SelectedDecks(prev => {
          const filtered = prev.filter(d => d.playerId !== player.id);
          return [...filtered, newDeckData];
        });
      } else {
        setTeam2SelectedDecks(prev => {
          const filtered = prev.filter(d => d.playerId !== player.id);
          return [...filtered, newDeckData];
        });
      }
    } else {
      // Si se selecciona un mazo específico
      const selectedDeck = decks.find(d => String(d.id) === deckId);
      if (selectedDeck) {
        const newDeckData = {
          playerId: player.id,
          deckId: selectedDeck.id,
          deckName: selectedDeck.name,
          deckRaza: selectedDeck.raza
        };

        if (teamNumber === 1) {
          setTeam1SelectedDecks(prev => {
            const filtered = prev.filter(d => d.playerId !== player.id);
            return [...filtered, newDeckData];
          });
        } else {
          setTeam2SelectedDecks(prev => {
            const filtered = prev.filter(d => d.playerId !== player.id);
            return [...filtered, newDeckData];
          });
        }
      }
    }
  }, [decks, player.id, teamNumber, setTeam1SelectedDecks, setTeam2SelectedDecks]);

  const selectValue = selectedDeck?.deckId === null ? 'any' : 
    (selectedDeck?.deckId && decks.some(d => String(d.id) === String(selectedDeck.deckId))
      ? String(selectedDeck.deckId)
      : '');

  return (
    <Box sx={{ mt: 1 }}>
      <Typography 
        variant="caption" 
        color="text.secondary"
        sx={{ 
          display: 'block',
          mb: 0.5,
          fontSize: '0.75rem',
          fontWeight: 500
        }}
      >
        Mazo
      </Typography>
      <FormControl 
        size="small" 
        fullWidth
        sx={{ 
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.5,
            borderColor: selectedDeck ? 'primary.main' : 'divider',
            '&:hover': {
              borderColor: 'primary.main'
            }
          }
        }}
      >
        <Select
          value={selectValue}
          onChange={handleDeckChange}
          displayEmpty
          disabled={loading}
          sx={{
            '& .MuiSelect-select': { py: 1 },
            fontSize: '0.875rem',
            bgcolor: 'background.paper'
          }}
        >
          <MenuItem value="" disabled>
            {loading ? 'Cargando mazos...' : error || 'Seleccionar mazo'}
          </MenuItem>
          {showAnyDeckOption && (
            <MenuItem 
              value="any"
              sx={{
                fontSize: '0.875rem',
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Chip
                label="Cualquier Mazo"
                size="small"
                sx={{
                  height: '20px',
                  fontSize: '0.7rem',
                  bgcolor: 'primary.main',
                  color: 'white',
                  '& .MuiChip-label': {
                    px: 0.5
                  }
                }}
              />
            </MenuItem>
          )}
          {decks.map((deck) => (
            <MenuItem 
              key={deck.id} 
              value={String(deck.id)}
              sx={{
                fontSize: '0.875rem',
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              {deck.is_default && (
                <Chip
                  label="Default"
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '0.7rem',
                    bgcolor: 'primary.main',
                    color: 'white',
                    '& .MuiChip-label': {
                      px: 0.5
                    }
                  }}
                />
              )}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                flex: 1
              }}>
                <Typography variant="body2">
                  {deck.name}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: '0.75rem',
                    lineHeight: 1
                  }}
                >
                  {deck.raza}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
});

// Main CustomPlayerInput component
const CustomPlayerInput = React.memo(({ 
  teamNumber,
  players,
  options,
  searchValue,
  handleSearch,
  handleSelectOption,
  selectedDecks,
  matchMode,
  activeInput,
  handleRemoveUser,
  setTeam1SelectedDecks,
  setTeam2SelectedDecks,
  setActiveInput,
  isSearching,
  showAnyDeckOption = false
}) => {
  const isTeam1 = teamNumber === 1;
  const maxPlayers = matchMode === '1v1' ? 1 : 2;
  const isTeamFull = players.length >= maxPlayers;
  const isDisabled = isTeamFull || (activeInput !== null && activeInput !== teamNumber);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Stack spacing={2}>
        <Paper
          sx={{
            p: '8px',
            pb: '6px',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <Stack spacing={1}>
            {players.map(player => (
              <Box key={player.id}>
                <Chip
                  avatar={<Avatar>
                    {(player.username || '?')[0]?.toUpperCase() || '?'}
                  </Avatar>}
                  label={player.username || 'Usuario'}
                  onDelete={() => handleRemoveUser(player.id, teamNumber)}
                  sx={{
                    borderRadius: 1,
                    '& .MuiChip-deleteIcon': {
                      color: 'error.main',
                      opacity: 0.55,
                      '&:hover': { 
                        color: 'error.dark',
                        opacity: 0.75,
                        transform: 'scale(1.1)'
                      }
                    }
                  }}
                />
              </Box>
            ))}

            {!isTeamFull && (
              <PlayerSearchInput
                onSearch={handleSearch}
                value={searchValue}
                disabled={isDisabled}
                setActiveInput={setActiveInput}
                teamNumber={teamNumber}
                isSearching={isSearching}
              />
            )}
          </Stack>
        </Paper>

        {/* Deck Selectors fuera del Paper principal */}
        {players.map(player => (
          <DeckSelector 
            key={player.id}
            player={player}
            teamNumber={teamNumber}
            selectedDeck={selectedDecks.find(d => d.playerId === player.id)}
            setTeam1SelectedDecks={setTeam1SelectedDecks}
            setTeam2SelectedDecks={setTeam2SelectedDecks}
            showAnyDeckOption={showAnyDeckOption}
          />
        ))}
      </Stack>

      {/* Resultados de búsqueda */}
      {options.length > 0 && !isTeamFull && !isDisabled && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            zIndex: 1,
            maxHeight: 200,
            overflowY: 'auto',
            borderRadius: 1,
            boxShadow: 3
          }}
        >
          {options.map(option => (
            <MenuItem
              key={option.id}
              onClick={() => handleSelectOption(option)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1
              }}
            >
              <Avatar sx={{ width: 24, height: 24 }}>
                {(option.username || '?')[0]?.toUpperCase() || '?'}
              </Avatar>
              <Typography>{option.username || 'Usuario'}</Typography>
            </MenuItem>
          ))}
        </Paper>
      )}
    </Box>
  );
});

export default CustomPlayerInput; 